import { HttpClient } from "./http";
import {
  API_PATH_TRACK_ORDER,
  API_PATH_TRACK_ADD_TO_CART,
  API_PATH_TRACK_REMOVE_FROM_CART,
  API_PATH_TRACK_PRODUCT_VIEW,
  API_PATH_TRACK_THANK_YOU_VIEW,
  API_PATH_TRACK_CHECKOUT,
  COOKIE_TOKEN,
} from "./config";
import type {
  ConversionData,
  ConversionResponse,
  TrackConversionResponse,
  AddToCartData,
  RemoveFromCartData,
  ProductViewData,
  ThankYouViewData,
  CheckoutData,
} from "../types";
import { Logger } from "./logger";
import { SessionManager } from "./session";
import { CookieManager } from "./cookie";

const STORAGE_KEY_TRACKED_ORDERS = "aff_tracked_orders";

export class ConversionTracker {
  private http: HttpClient;
  private trackedOrders = new Set<string>();
  // Scoped per API key so other SDK builds on the same page (they share this
  // code and the base key) cannot mark an order as tracked for this instance.
  private storageKey: string;

  constructor(http: HttpClient, apiKey: string) {
    this.http = http;
    this.storageKey = `${STORAGE_KEY_TRACKED_ORDERS}_${apiKey}`;
    this.loadTrackedOrders();
  }

  private loadTrackedOrders(): void {
    const stored = this.getStoredTrackedOrders();
    stored.forEach((id) => this.trackedOrders.add(id));
  }

  private getStoredTrackedOrders(): string[] {
    if (typeof window === "undefined" || !window.sessionStorage) return [];
    try {
      const stored = window.sessionStorage.getItem(this.storageKey);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  }

  private isOrderTracked(orderId: string): boolean {
    if (this.trackedOrders.has(orderId)) return true;

    const stored = this.getStoredTrackedOrders();
    return stored.includes(orderId);
  }

  private markOrderTracked(orderId: string): void {
    this.trackedOrders.add(orderId);

    if (typeof window !== "undefined" && window.sessionStorage) {
      try {
        const stored = this.getStoredTrackedOrders();
        if (!stored.includes(orderId)) {
          stored.push(orderId);
          window.sessionStorage.setItem(this.storageKey, JSON.stringify(stored));
        }
      } catch (err) {
        Logger.warn("Failed to save tracked order to sessionStorage", err);
      }
    }
  }

  private getEffectiveToken(cookieToken: string | null): string {
    if (cookieToken) return cookieToken;

    const savedToken = CookieManager.get(COOKIE_TOKEN);
    if (savedToken) {
      Logger.warn("Using fallback cookie_token from COOKIE_TOKEN");
      return savedToken;
    }

    const sessionId = SessionManager.getOrCreate();
    Logger.warn("No cookie_token or ref found — using session_id as fallback token");
    return sessionId;
  }

  async trackOrder(
    orderData: ConversionData,
    cookieToken: string | null,
    isAttributed: boolean,
    locationId?: string | null,
    locationName?: string | null,
  ): Promise<ConversionResponse | null> {
    try {
      if (!isAttributed) {
        Logger.warn("trackOrder: no affiliate attribution detected — server will decide");
      }

      if (!orderData.orderId || typeof orderData.orderId !== "string") {
        Logger.error("trackOrder: missing or invalid orderId");
        return null;
      }

      if (this.isOrderTracked(orderData.orderId)) {
        Logger.warn("Order already tracked, skipping duplicate");
        return null;
      }

      if (!orderData.customer?.email || typeof orderData.customer.email !== "string") {
        Logger.warn(
          "trackOrder: customer.email missing or empty — proceeding with anonymous customer tracking",
        );
      }

      const sessionId = SessionManager.getOrCreate();
      const effectiveToken = this.getEffectiveToken(cookieToken);

      const postOptions: { useSecret: boolean; params?: Record<string, string> } = {
        useSecret: true,
      };
      if (orderData.payment) {
        postOptions.params = { payment: orderData.payment };
      }

      const orderPayload: Record<string, unknown> = {
        event_id: `order_${orderData.orderId}`,
        session_id: sessionId,
        cookie_token: effectiveToken,
        order_id: orderData.orderId,
        order_total: orderData.total,
        subtotal: orderData.subtotal,
        discount_applied: orderData.discount ?? 0,
        tax: orderData.tax,
        customer: orderData.customer,
        line_items: orderData.lineItems ?? [],
        discount_code: orderData.couponCode ?? undefined,
      };
      if (locationId) orderPayload.location_id = locationId;
      if (locationName) orderPayload.location_name = locationName;
      const response = await this.http.post<TrackConversionResponse>(
        API_PATH_TRACK_ORDER,
        orderPayload,
        postOptions,
      );

      this.markOrderTracked(orderData.orderId);

      Logger.log("Order tracked", response);

      return {
        conversionId: response.conversion_id,
        commissionAmount: response.commission_amount,
        affiliateId: response.affiliate_id,
      };
    } catch (err) {
      Logger.error("Order tracking failed", err);
      return null;
    }
  }

  async trackCheckout(
    data: CheckoutData,
    cookieToken: string | null,
    locationId?: string | null,
    locationName?: string | null,
  ): Promise<void> {
    try {
      const sessionId = SessionManager.getOrCreate();
      const effectiveToken = this.getEffectiveToken(cookieToken);
      const eventId = data.cartId ? `checkout_${data.cartId}` : `checkout_${sessionId}`;

      const checkoutPayload: Record<string, unknown> = {
        event_id: eventId,
        session_id: sessionId,
        cookie_token: effectiveToken,
        items: data.items,
        total: data.total,
        currency: data.currency,
        discount_code: data.couponCode ?? undefined,
        cart_id: data.cartId ?? undefined,
      };
      if (locationId) checkoutPayload.location_id = locationId;
      if (locationName) checkoutPayload.location_name = locationName;
      await this.http.post(API_PATH_TRACK_CHECKOUT, checkoutPayload);
      Logger.log("Checkout tracked", data);
    } catch (err) {
      Logger.error("Checkout tracking failed", err);
    }
  }

  async trackAddToCart(
    data: AddToCartData,
    cookieToken: string | null,
    locationId?: string | null,
    locationName?: string | null,
  ): Promise<void> {
    try {
      const sessionId = SessionManager.getOrCreate();
      const effectiveToken = this.getEffectiveToken(cookieToken);
      const eventId = `add_to_cart_${data.cartId || Date.now()}`;

      const addToCartPayload: Record<string, unknown> = {
        event_id: eventId,
        session_id: sessionId,
        cookie_token: effectiveToken,
        items: data.items,
        cart_id: data.cartId,
      };
      if (locationId) addToCartPayload.location_id = locationId;
      if (locationName) addToCartPayload.location_name = locationName;
      await this.http.post(API_PATH_TRACK_ADD_TO_CART, addToCartPayload);
      Logger.log("Add to cart tracked", data);
    } catch (err) {
      Logger.error("Add to cart tracking failed", err);
    }
  }

  async trackRemoveFromCart(
    data: RemoveFromCartData,
    cookieToken: string | null,
    locationId?: string | null,
    locationName?: string | null,
  ): Promise<void> {
    try {
      const sessionId = SessionManager.getOrCreate();
      const effectiveToken = this.getEffectiveToken(cookieToken);
      const eventId = `remove_from_cart_${data.cartId || Date.now()}`;

      const removeFromCartPayload: Record<string, unknown> = {
        event_id: eventId,
        session_id: sessionId,
        cookie_token: effectiveToken,
        items: data.items,
        cart_id: data.cartId,
      };
      if (locationId) removeFromCartPayload.location_id = locationId;
      if (locationName) removeFromCartPayload.location_name = locationName;
      await this.http.post(API_PATH_TRACK_REMOVE_FROM_CART, removeFromCartPayload);
      Logger.log("Remove from cart tracked", data);
    } catch (err) {
      Logger.error("Remove from cart tracking failed", err);
    }
  }

  async trackProductDetailsViewed(
    data: ProductViewData,
    cookieToken: string | null,
    locationId?: string | null,
    locationName?: string | null,
  ): Promise<void> {
    try {
      const sessionId = SessionManager.getOrCreate();
      const effectiveToken = this.getEffectiveToken(cookieToken);

      const productViewPayload: Record<string, unknown> = {
        event_id: `product_view_${data.product.productId}`,
        session_id: sessionId,
        cookie_token: effectiveToken,
        product: data.product,
      };
      if (locationId) productViewPayload.location_id = locationId;
      if (locationName) productViewPayload.location_name = locationName;
      await this.http.post(API_PATH_TRACK_PRODUCT_VIEW, productViewPayload);
      Logger.log("Product details viewed tracked", data);
    } catch (err) {
      Logger.error("Product details viewed tracking failed", err);
    }
  }

  async trackThankYouPageViewed(
    data: ThankYouViewData,
    cookieToken: string | null,
    locationId?: string | null,
    locationName?: string | null,
  ): Promise<void> {
    try {
      const sessionId = SessionManager.getOrCreate();
      const effectiveToken = this.getEffectiveToken(cookieToken);

      const thankYouPayload: Record<string, unknown> = {
        event_id: `thankyou_${data.orderId}`,
        session_id: sessionId,
        cookie_token: effectiveToken,
        order_id: data.orderId,
        customer: data.customer,
      };
      if (locationId) thankYouPayload.location_id = locationId;
      if (locationName) thankYouPayload.location_name = locationName;
      await this.http.post(API_PATH_TRACK_THANK_YOU_VIEW, thankYouPayload);

      Logger.warn(
        "Thank you page tracked. Make sure trackOrder() was called after payment success.",
      );
      Logger.log("Thank you page viewed tracked", data);
    } catch (err) {
      Logger.error("Thank you page viewed tracking failed", err);
    }
  }
}
